import { useEffect, useRef } from 'react';
import { formatDistance, formatDuration, formatMass, formatSpeed, RAD } from '@/core/math/scalar';
import { GameSnapshot } from '@/game/Snapshot';
import { HudVisibility } from './HudSections';

interface RowProps {
  label: string;
  value: string;
  tone?: 'good' | 'warn' | 'bad';
}

function Row({ label, value, tone }: RowProps): JSX.Element {
  return (
    <div className="row">
      <span className="label">{label}</span>
      <span className={`value${tone ? ` ${tone}` : ''}`}>{value}</span>
    </div>
  );
}

function Bar({ value, tone }: { value: number; tone?: 'good' | 'warn' | 'bad' }): JSX.Element {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className={`bar${tone ? ` ${tone}` : ''}`}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * Attitude and navigation ball.
 *
 * Drawn to a canvas rather than to the DOM, because it updates every frame and
 * a DOM-based instrument at 60 Hz is the fastest way to make a browser game
 * stutter. It reads the same snapshot as everything else.
 */
function NavBall({ snapshot }: { snapshot: GameSnapshot }): JSX.Element {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = 150;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);

    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 8;

    const nav = snapshot.navigation;

    /*
     * Real attitude, not a fake stand-in.
     *
     * `radialOut` is the local vertical (planet up) as seen from the
     * vessel's own axes (+X port, +Y dorsal, +Z nose) — exactly what a
     * physical navball's gimbal reads. Wings level and nose horizontal, that
     * vector points straight dorsal (0,1,0); pitching the nose up swings it
     * toward the nose axis, and rolling swings it toward port/starboard. So
     * pitch and roll both fall straight out of this one vector, with no need
     * to fake pitch from vertical speed (which used to leave the ball dead
     * level for a vessel pitched but not yet climbing, e.g. sitting tilted
     * on a slope) and no roll at all (the horizon line never rotated).
     */
    const [ux, uy, uz] = nav.bodyFrame.radialOut;
    const pitch = Math.asin(Math.max(-1, Math.min(1, uz)));
    const roll = Math.atan2(ux, uy);

    // Horizon disc: sky above, ground below, offset by pitch and rotated by
    // roll around the ball's centre — a real navball's horizon does both.
    const horizonOffset = (pitch / (Math.PI / 2)) * r * 0.8;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.translate(cx, cy);
    ctx.rotate(roll);

    ctx.fillStyle = '#16324a';
    ctx.fillRect(-size, -size, size * 2, size + horizonOffset);
    ctx.fillStyle = '#3a2c1e';
    ctx.fillRect(-size, horizonOffset, size * 2, size * 2);

    ctx.strokeStyle = 'rgba(200,225,255,0.55)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-size, horizonOffset);
    ctx.lineTo(size, horizonOffset);
    ctx.stroke();

    // Pitch ladder.
    ctx.strokeStyle = 'rgba(200,225,255,0.22)';
    for (let i = -4; i <= 4; i++) {
      if (i === 0) continue;
      const y = horizonOffset + i * r * 0.2;
      const w = i % 2 === 0 ? r * 0.34 : r * 0.2;
      ctx.beginPath();
      ctx.moveTo(-w, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.restore();

    /*
     * Heading: the compass bearing the nose points at, projected onto the
     * local horizontal plane. `north` only needs to be roughly perpendicular
     * to `up` (it is, by construction, but floating point makes "roughly"
     * the honest word) so it is re-projected here rather than trusted as
     * exact. East is derived as up × north-horizontal so the result is a
     * right-handed, standard-orientation ENU frame regardless of hemisphere.
     */
    const upV = { x: ux, y: uy, z: uz };
    const [nx0, ny0, nz0] = nav.bodyFrame.north;
    const northDot = nx0 * upV.x + ny0 * upV.y + nz0 * upV.z;
    const nh = { x: nx0 - upV.x * northDot, y: ny0 - upV.y * northDot, z: nz0 - upV.z * northDot };
    const nhLen = Math.hypot(nh.x, nh.y, nh.z);
    if (nhLen > 1e-6) {
      nh.x /= nhLen;
      nh.y /= nhLen;
      nh.z /= nhLen;
      // East = up x north (right-handed).
      const eh = {
        x: upV.y * nh.z - upV.z * nh.y,
        y: upV.z * nh.x - upV.x * nh.z,
        z: upV.x * nh.y - upV.y * nh.x,
      };
      // Nose (0,0,1 in body frame) projected onto the same horizontal plane.
      const noseDot = upV.z; // (0,0,1) . up
      const nose = { x: -upV.x * noseDot, y: -upV.y * noseDot, z: 1 - upV.z * noseDot };
      const noseLen = Math.hypot(nose.x, nose.y, nose.z);
      if (noseLen > 1e-6) {
        const noseEast = (nose.x * eh.x + nose.y * eh.y + nose.z * eh.z) / noseLen;
        const noseNorth = (nose.x * nh.x + nose.y * nh.y + nose.z * nh.z) / noseLen;
        const headingDeg = ((Math.atan2(noseEast, noseNorth) * 180) / Math.PI + 360) % 360;
        ctx.fillStyle = 'rgba(230,244,255,0.9)';
        ctx.font = '10px ui-monospace, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(`${headingDeg.toFixed(0).padStart(3, '0')}°`, cx, cy - r - 2);
      }
    }

    // Rim.
    ctx.strokeStyle = 'rgba(120,170,220,0.45)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    /*
     * Velocity markers, at the bearing each reference direction actually lies
     * on relative to the nose.
     *
     * The snapshot hands over unit vectors already rotated into the vessel's
     * body frame (+X port, +Y dorsal, +Z nose), so all that is left here is
     * the screen convention: canvas +x is to the right, which is starboard and
     * therefore -X in the body frame, and canvas +y is down, which is -Y.
     * A direction in front of the nose is projected orthographically inside
     * the disc, one behind it is pinned to the rim on the same bearing and
     * drawn faint — the same "it is back there somewhere" reading a real ball
     * gives by rotating the marker out of sight.
     */
    const bf = nav.bodyFrame;
    const opposite = (d: readonly [number, number, number]): [number, number, number] => [-d[0], -d[1], -d[2]];
    const markers: { label: string; color: string; dir: readonly [number, number, number] }[] = [
      { label: 'P', color: '#a6e22e', dir: bf.prograde },
      { label: 'R', color: '#e6a5ff', dir: opposite(bf.prograde) },
      { label: 'N', color: '#f0a0d0', dir: bf.normal },
      { label: 'U', color: '#5ec8f0', dir: bf.radialOut },
    ];
    ctx.font = '9px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const m of markers) {
      const [bx, by, bz] = m.dir;
      const planar = Math.hypot(bx, by);
      const bearing = planar > 1e-9 ? Math.atan2(-by, -bx) : 0;
      const behind = bz < 0;
      const distance = behind ? r - 9 : r * 0.86 * planar;
      const mx = cx + Math.cos(bearing) * distance;
      const my = cy + Math.sin(bearing) * distance;
      ctx.globalAlpha = behind ? 0.4 : 1;
      ctx.strokeStyle = m.color;
      ctx.beginPath();
      ctx.arc(mx, my, 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = m.color;
      ctx.fillText(m.label, mx, my + 0.5);
    }
    ctx.globalAlpha = 1;

    // Vessel reference: fixed cross at the centre.
    ctx.strokeStyle = '#eaf4ff';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(cx - 14, cy);
    ctx.lineTo(cx - 5, cy);
    ctx.moveTo(cx + 5, cy);
    ctx.lineTo(cx + 14, cy);
    ctx.moveTo(cx, cy - 5);
    ctx.lineTo(cx, cy - 10);
    ctx.stroke();
  }, [snapshot]);

  return (
    <div className="navball-wrap">
      <canvas ref={ref} className="navball" style={{ width: 150, height: 150 }} />
    </div>
  );
}

function toneForFraction(value: number, warn = 0.35, bad = 0.15): 'good' | 'warn' | 'bad' {
  if (value <= bad) return 'bad';
  if (value <= warn) return 'warn';
  return 'good';
}

/**
 * Flight, navigation, environment and warp readouts.
 *
 * Each group is drawn only when `visibility` says so, and the two wrappers are
 * skipped entirely when every group inside them is off — an empty `.hud-top`
 * is invisible on desktop (`display: contents`) but is a real, gap-carrying
 * flex box at the mobile breakpoint, where it would reserve space for nothing.
 */
export function Hud({
  snapshot,
  visibility,
}: {
  snapshot: GameSnapshot;
  visibility: HudVisibility;
}): JSX.Element {
  const v = snapshot.vessel;
  const nav = snapshot.navigation;
  const env = snapshot.environment;
  const warp = snapshot.warp;
  const thermalRatio = v.hullTemperature / Math.max(1, v.hullTemperatureLimit);

  const free = snapshot.player.freeFlight;
  const showTop = visibility.vessel || visibility.navigation;
  const showBottom = visibility.environment || visibility.flight || visibility.time;
  /*
   * A top readout whose column has nothing below it may use the whole column.
   *
   * On a short screen the stylesheet caps each corner at half the vertical
   * budget, because `.hud-top-left` grows down from the launcher row and
   * `.hud-bottom-left` grows up from the control band through the same
   * pixels and neither can see the other. Half is the only safe cap CSS can
   * pick — but React knows which groups are actually switched on, and half of
   * a 153px budget is a navigation panel showing three of its ten rows for no
   * reason when the bottom of that column is empty.
   *
   * The left column counts the navball group as well as the environment
   * readout: on a small landscape phone the navball group is lifted out of the
   * bottom centre and left-aligned above the control band (see the
   * 780px-and-short block in styles.css), which puts it in this column.
   */
  const soloLeft = !visibility.environment && !visibility.flight;
  const soloRight = !visibility.time;

  return (
    <>
      {free && (
        <div className="freeflight">
          FREE FLIGHT &nbsp; {formatSpeed(free.speed)}
          {/* Says why the camera is slower than the wheel was set to, rather
              than leaving it looking like the control stopped responding — and
              distinguishes that from the pilot deliberately pushing through. */}
          {free.speedOverridden && <span className="tag"> &nbsp;override</span>}
          {free.speedLimited && !free.speedOverridden && (
            <span className="warn"> &nbsp;(near surface — wheel up to override)</span>
          )}
          &nbsp;·&nbsp; wheel or +/- to change speed &nbsp;·&nbsp; shift boosts &nbsp;·&nbsp; F8 exit
        </div>
      )}

      {/* --- Vessel + navigation --------------------------------------------
          Wrapped so a mobile breakpoint can stack them instead of placing
          both in their own absolutely-positioned corner, where two ~250px+
          panels cannot coexist side by side under ~500px of viewport width.
          `display: contents` on desktop makes the wrapper invisible to layout
          entirely, so the corner positioning below is completely unchanged
          there — see .hud-top in styles.css. */}
      {showTop && (
      <div className="hud-top">
      {visibility.vessel && (
      <div className={`hud-top-left panel${soloLeft ? ' solo-column' : ''}`}>
        <div className="panel-title">
          <span>{v.name}</span>
          <span>{v.className}</span>
        </div>
        <Row label="Mass" value={formatMass(v.mass)} />
        <Row label="Delta-v" value={`${v.deltaV.toFixed(0)} m/s`} />
        <Row label="Thrust" value={`${(v.thrust / 1000).toFixed(1)} kN`} />
        <Row label="TWR" value={v.thrustToWeight.toFixed(2)} tone={v.thrustToWeight >= 1 ? 'good' : 'warn'} />
        <Row label="G-force" value={`${v.gForce.toFixed(2)} g`} tone={v.gForce > 6 ? 'bad' : undefined} />

        <div className="panel-title" style={{ marginTop: 10 }}>
          <span>Propellant</span>
          <span>{(v.propellantFraction * 100).toFixed(0)}%</span>
        </div>
        <Bar value={v.propellantFraction} tone={toneForFraction(v.propellantFraction, 0.25, 0.08)} />

        <div className="panel-title">
          <span>Battery</span>
          <span>{(v.batteryFraction * 100).toFixed(0)}%</span>
        </div>
        <Bar value={v.batteryFraction} tone={toneForFraction(v.batteryFraction, 0.3, 0.12)} />

        <div className="panel-title">
          <span>Hull</span>
          <span>{v.hullTemperature.toFixed(0)} K</span>
        </div>
        <Bar
          value={thermalRatio}
          tone={thermalRatio > 0.85 ? 'bad' : thermalRatio > 0.6 ? 'warn' : 'good'}
        />

        {v.brownout && <div className="tag bad">Power brownout</div>}
        {v.hullDamage > 0.02 && <div className="tag bad">Hull damage {(v.hullDamage * 100).toFixed(0)}%</div>}
        {v.gearDamage > 0.02 && <div className="tag warn">Gear damage {(v.gearDamage * 100).toFixed(0)}%</div>}
        {v.wingDamage > 0.02 && <div className="tag warn">Wing damage {(v.wingDamage * 100).toFixed(0)}%</div>}
      </div>
      )}

      {/* --- Navigation --------------------------------------------------- */}
      {visibility.navigation && (
      <div className={`hud-top-right${soloRight ? ' solo-column' : ''}`}>
        <div className="panel">
          {free ? (
            // Free flight detaches the camera from the vessel entirely (see
            // FreeFlight.ts), so the vessel's orbital elements below — apoapsis,
            // periapsis, eccentricity — describe a body this panel is no longer
            // looking from. Camera position/altitude in its place, or every
            // debugging session loses track of where free flight actually is
            // the moment it drifts from the vessel, which is the whole reason to
            // use it.
            <>
              <div className="panel-title">
                <span>{free.bodyName}</span>
                <span>free flight</span>
              </div>
              <Row label="Altitude" value={formatDistance(free.altitude)} />
              <Row label="Alt (sea level)" value={formatDistance(free.altitudeAboveSeaLevel)} />
              <Row label="Speed" value={formatSpeed(free.speed)} />
              <Row
                label="Position"
                value={`${(free.latitude * RAD).toFixed(5)}, ${(free.longitude * RAD).toFixed(5)}`}
              />
              {/* Position alone does not describe a view. Heading and pitch are
                  what make a reported sighting reproducible from a screenshot. */}
              <Row
                label="Looking"
                value={`${free.heading.toFixed(1)} deg, ${free.pitch >= 0 ? '+' : ''}${free.pitch.toFixed(1)} deg`}
              />
            </>
          ) : (
            <>
              <div className="panel-title">
                <span>{nav.referenceBodyName}</span>
                <span>{v.landed ? 'landed' : 'flight'}</span>
              </div>
              <Row label="Altitude" value={formatDistance(nav.altitude)} />
              <Row label="Orbital speed" value={formatSpeed(nav.orbitalSpeed)} />
              <Row label="Surface speed" value={formatSpeed(nav.surfaceSpeed)} />
              <Row
                label="Vertical"
                value={formatSpeed(nav.verticalSpeed)}
                tone={nav.verticalSpeed < -40 && nav.altitude < 3000 ? 'bad' : undefined}
              />
              <Row label="Apoapsis" value={Number.isFinite(nav.apoapsis) ? formatDistance(nav.apoapsis) : 'escape'} />
              <Row
                label="Periapsis"
                value={formatDistance(nav.periapsis)}
                tone={nav.periapsis < 0 ? 'warn' : undefined}
              />
              <Row
                label="Period"
                value={Number.isFinite(nav.orbitalPeriod) ? formatDuration(nav.orbitalPeriod) : '--'}
              />
              <Row label="Eccentricity" value={nav.eccentricity.toFixed(4)} />
              <Row label="Inclination" value={`${(nav.inclination * RAD).toFixed(2)} deg`} />
              <Row
                label="Position"
                value={`${(nav.latitude * RAD).toFixed(5)}, ${(nav.longitude * RAD).toFixed(5)}`}
              />
            </>
          )}
        </div>

        {nav.events.length > 0 && (
          <div className="panel">
            <div className="panel-title">
              <span>Predicted events</span>
            </div>
            {nav.events.map((e, i) => (
              <Row
                key={`${e.kind}-${i}`}
                label={e.label}
                value={formatDuration(e.timeUntil)}
                tone={e.kind === 'impact' ? 'bad' : e.kind === 'atmosphereEntry' ? 'warn' : undefined}
              />
            ))}
          </div>
        )}

        {nav.maneuverNodes.length > 0 && (
          <div className="panel">
            <div className="panel-title">
              <span>Manoeuvre plan</span>
            </div>
            {nav.maneuverNodes.map((n) => (
              <div key={n.id}>
                <Row label={n.label} value={`${n.deltaV.toFixed(1)} m/s`} />
                <Row label="  in" value={formatDuration(n.timeUntil)} />
                <Row label="  burn" value={formatDuration(n.burnDuration)} />
              </div>
            ))}
          </div>
        )}
      </div>
      )}
      </div>
      )}

      {/* --- Environment + flight + warp -------------------------------------
          Same wrapping trick as .hud-top, and it matters more here: three
          groups (environment, navball+flight, time/warp) share the bottom
          edge, and the navball group alone is wider than a phone screen once
          you add the panel beside it. See .hud-bottom in styles.css. */}
      {showBottom && (
      <div className="hud-bottom">
      {visibility.environment && (
      <div className="hud-bottom-left panel">
        <div className="panel-title">
          <span>{env.bodyName}</span>
          <span>{env.biome}</span>
        </div>
        <Row label="Gravity" value={`${env.gravity.toFixed(2)} m/s2`} />
        <Row label="Temperature" value={`${env.temperature.toFixed(0)} K`} />
        <Row label="Pressure" value={env.pressure > 0 ? `${(env.pressure / 1000).toFixed(2)} kPa` : 'vacuum'} />
        <Row label="Density" value={env.density > 0 ? `${env.density.toFixed(4)} kg/m3` : '0'} />
        <Row
          label="Dyn. pressure"
          value={`${(env.dynamicPressure / 1000).toFixed(2)} kPa`}
          tone={env.dynamicPressure > 40000 ? 'warn' : undefined}
        />
        <Row label="Mach" value={env.mach > 0.05 ? env.mach.toFixed(2) : '--'} />
        <Row label="Irradiance" value={`${env.irradiance.toFixed(0)} W/m2`} />
        <Row
          label="Radiation"
          value={`${(env.radiation * 1000).toFixed(3)} mSv/h`}
          tone={env.radiation > 0.01 ? 'bad' : env.radiation > 0.001 ? 'warn' : undefined}
        />
        <Row label="Breathable" value={env.breathable ? 'yes' : 'no'} tone={env.breathable ? 'good' : 'warn'} />
        <Row label="Sun elevation" value={`${(env.sunElevation * RAD).toFixed(1)} deg`} />
      </div>
      )}

      {/* --- Navball and flight state --------------------------------------- */}
      {visibility.flight && (
      <div className="hud-bottom-centre">
        <NavBall snapshot={snapshot} />
        <div className="panel flight-panel">
          <div className="panel-title">
            <span>Flight systems</span>
            <span>{v.alignment !== 'none' ? v.alignment : 'manual'}</span>
          </div>
          <div className="button-row" style={{ marginBottom: 6 }}>
            <span className={`tag${v.engineGroups.main ? ' good' : ''}`}>Main</span>
            <span className={`tag${v.engineGroups.atmospheric ? ' good' : ''}`}>Atmo</span>
            <span className={`tag${v.engineGroups.landing ? ' good' : ''}`}>Landing</span>
            <span className={`tag${v.holds.rotationDamping ? ' good' : ''}`}>SAS</span>
            {v.holds.hoverAssist && <span className="tag good">Hover</span>}
            {v.holds.altitudeHold && <span className="tag good">Alt hold</span>}
          </div>
          <div className="panel-title">
            <span>Throttle</span>
            <span>{(v.throttle * 100).toFixed(0)}%</span>
          </div>
          <Bar value={v.throttle} />

          {v.fieldDrive.present && (
            <>
              <div className="panel-title" style={{ marginTop: 6 }}>
                <span>Field drive</span>
                <span>{v.fieldDrive.mode}</span>
              </div>
              <Row label="Output" value={`${v.fieldDrive.acceleration.toFixed(2)} m/s2`} />
              <Row label="Efficiency" value={`${(v.fieldDrive.efficiency * 100).toFixed(0)}%`} />
              <Row
                label="Coil temp"
                value={`${v.fieldDrive.temperature.toFixed(0)} K`}
                tone={v.fieldDrive.temperature > v.fieldDrive.thermalLimit ? 'bad' : undefined}
              />
              <Row label="Power draw" value={`${(v.fieldDrive.power / 1000).toFixed(0)} kW`} />
              {v.fieldDrive.limitReason && <div className="tag warn">{v.fieldDrive.limitReason}</div>}
            </>
          )}

          {v.assistFailures.map((f, i) => (
            <div key={i} className="tag bad">
              {f.system}: {f.reason}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* --- Warp and macro ------------------------------------------------- */}
      {visibility.time && (
      <div className="hud-bottom-right panel">
        <div className="panel-title">
          <span>Time</span>
          <span>{formatDuration(snapshot.simulationTime)}</span>
        </div>
        <div className="warp-indicator">
          <span className="warp-rate">{warp.rate}x</span>
          <span className="label">{warp.mode}</span>
        </div>
        {warp.restriction && <div className="tag warn">{warp.restriction}</div>}
        {warp.rate < warp.maxRate && <div className="label">max available {warp.maxRate}x</div>}

        {snapshot.macro && (
          <>
            <div className="panel-title" style={{ marginTop: 10 }}>
              <span>{snapshot.macro.name}</span>
              <span>{snapshot.macro.status}</span>
            </div>
            <div className="label" style={{ whiteSpace: 'normal', lineHeight: 1.5 }}>
              {snapshot.macro.statusText}
            </div>
            <div className="macro-progress">
              {Array.from({ length: snapshot.macro.phaseCount }, (_, i) => (
                <div
                  key={i}
                  className={`macro-phase${
                    i < snapshot.macro!.phaseIndex ? ' done' : i === snapshot.macro!.phaseIndex ? ' active' : ''
                  }`}
                />
              ))}
            </div>
            <Row label="Elapsed" value={formatDuration(snapshot.macro.elapsed)} />
            <Row label="Propellant used" value={formatMass(snapshot.macro.propellantUsed)} />
          </>
        )}
      </div>
      )}
      </div>
      )}

      {/* --- Reticle -------------------------------------------------------- */}
      {visibility.reticle && (
        <div className="hud-centre">
          <svg className="reticle" viewBox="0 0 28 28" fill="none" stroke="#cfe3ff" strokeWidth="1">
            <circle cx="14" cy="14" r="2.5" />
            <path d="M14 0v6M14 22v6M0 14h6M22 14h6" />
          </svg>
        </div>
      )}
    </>
  );
}
